import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useGlobalContext } from '~/context/GlobalContext';
import { useWebRTC } from '~/hooks/useWebRTC';
import { ICallSession, IRouletteFilters } from '~/types/common';
import {
  CameraIcon, CameraMuteIcon,
  MicrophoneIcon, MicrophoneMuteIcon,
  VoiceIcon, VoiceMuteIcon,
  ChatIcon, CloseIcon,
} from '~/components/Icons/Icons';
import RouletteChat from '~/componentsPage/Roulette/RouletteChat';
import RouletteFilters from '~/componentsPage/Roulette/RouletteFilters';
import styles from './styles/RouletteContainer.module.css';

const SWIPE_THRESHOLD_PX = 60;
const WHEEL_THRESHOLD_PX = 80;
const NEXT_COOLDOWN_MS = 700;

function RouletteContainer() {
  const navigate = useNavigate();
  const { context, setContext } = useGlobalContext();
  const { localVideoRef, remoteVideoRef, startCall, cleanup } = useWebRTC();

  const [isMicOn, setIsMicOn] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const lastNextAt = useRef<number>(0);
  const touchStartY = useRef<number | null>(null);
  const wheelAccum = useRef<number>(0);

  const status = context.roulette.status;
  const partner = context.roulette.partner;
  const roomId = context.roulette.roomId;
  const isRegistered = !!context.authUser;

  const requestNext = useCallback(() => {
    const now = Date.now();
    if (now - lastNextAt.current < NEXT_COOLDOWN_MS) return;
    lastNextAt.current = now;
    try { cleanup(); } catch (e) { /* ignore */ }
    window.dispatchEvent(new CustomEvent('rouletteNext'));
  }, [cleanup]);

  // ── Inicjacja WebRTC po sparowaniu ─────────────────────────────────────
  useEffect(() => {
    if (status !== 'matched' || !partner || !roomId) return;
    const myId = context.user?.id;
    if (!myId) return;

    const isInitiator = context.roulette.isInitiator;

    if (isInitiator) {
      // Wzorem VideoContainer.callToUser: emit webRTC callToUser; backend prześle userCalling do partnera.
      const data: ICallSession = {
        event: 'callToUser',
        initiatorId: myId,
        initiatorName: context.user?.name || '',
        partnerId: partner.id,
        partnerName: partner.name,
        roomId,
        isUserBusy: true,
      };
      window.dispatchEvent(new CustomEvent('webRTC', { detail: data }));
    }
    // W obu przypadkach rejestrujemy listener userCalling — odbiorca auto-akceptuje, inicjator startuje od razu.
  }, [status, partner?.id, roomId, context.roulette.isInitiator]);

  // Auto-akceptacja połączenia po stronie odbiorcy
  useEffect(() => {
    const handleUserCalling = (e: any) => {
      const incoming = e.detail as ICallSession;
      if (status !== 'matched' || !roomId || incoming?.roomId !== roomId) return;

      // Zaakceptuj automatycznie — żadnego ekranu „odbierz".
      setContext(prev => ({
        ...prev,
        callSession: {
          event: 'callSessionAccept',
          initiatorId: incoming.initiatorId,
          initiatorName: incoming.initiatorName,
          partnerId: incoming.partnerId,
          partnerName: incoming.partnerName,
          roomId: incoming.roomId,
          isUserBusy: true,
        },
      }));

      window.dispatchEvent(new CustomEvent('callSessionAccept', {
        detail: { ...incoming, event: 'callSessionAccept' }
      }));
      startCall();
    };
    window.addEventListener('userCalling', handleUserCalling);
    return () => window.removeEventListener('userCalling', handleUserCalling);
  }, [status, roomId, setContext, startCall]);

  // Cleanup przy odejściu od strony
  useEffect(() => {
    return () => {
      try { cleanup(); } catch (e) { /* ignore */ }
      window.dispatchEvent(new CustomEvent('rouletteLeave'));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-rozłączenie po wyjściu partnera + przekazanie do kolejnego matchu
  useEffect(() => {
    const onPartnerLeft = () => {
      try { cleanup(); } catch (e) { /* ignore */ }
      // Backend automatycznie wstawia z powrotem do kolejki — czekamy na rouletteMatched lub rouletteWaiting.
    };
    window.addEventListener('roulettePartnerLeft', onPartnerLeft);
    return () => window.removeEventListener('roulettePartnerLeft', onPartnerLeft);
  }, [cleanup]);

  // Badge dla niewidocznego czatu — przy nowej wiadomości otwieramy slide-up.
  useEffect(() => {
    // 1. Sprawdzamy, czy jesteśmy w trakcie rozmowy w ruletce
    if (status !== 'matched' || !roomId) return;

    // 2. KLUCZOWE: Upewniamy się, że obiekt 'room' to nasz pokój ruletki, 
    // a nie "resztki" załadowane z publicznego czatu.
    if (context.room?.id !== roomId) return;

    const messages = context.room?.messages;
    if (!messages || messages.length === 0) return;

    const last = messages[messages.length - 1];

    // 3. Ignorujemy nasze własne wiadomości
    if (last.isMine) return;

    // 4. Otwieramy czat tylko na faktyczną wiadomość od partnera (posiadającą userId).
    // Używamy callbacku stanu, aby uniknąć błędu z niemożnością zamknięcia czatu (yo-yo effect).
    if (last.userId) {
      setIsChatOpen((prevIsOpen) => {
        if (prevIsOpen) return true;
        return true;
      });
    }
  }, [context.room?.messages?.length, status, roomId, context.room?.id]);

  // ── Swipe / wheel ──────────────────────────────────────────────────────
  const onTouchStart = (e: React.TouchEvent) => {
    if (isChatOpen) return;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartY.current === null) return;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartY.current = null;
    if (Math.abs(dy) > SWIPE_THRESHOLD_PX) requestNext();
  };
  const onWheel = (e: React.WheelEvent) => {
    if (isChatOpen) return;
    wheelAccum.current += e.deltaY;
    if (Math.abs(wheelAccum.current) > WHEEL_THRESHOLD_PX) {
      wheelAccum.current = 0;
      requestNext();
    }
  };

  // ── Kontrolki mediów ────────────────────────────────────────────────────
  const toggleMic = (e: React.MouseEvent) => {
    e.stopPropagation();
    const stream = (localVideoRef.current?.srcObject as MediaStream) || null;
    const tracks = stream?.getAudioTracks() || [];
    if (tracks.length > 0) {
      tracks[0].enabled = !isMicOn;
      setIsMicOn(!isMicOn);
    }
  };
  const toggleCamera = (e: React.MouseEvent) => {
    e.stopPropagation();
    const stream = (localVideoRef.current?.srcObject as MediaStream) || null;
    const v = stream?.getVideoTracks() || [];
    const a = stream?.getAudioTracks() || [];
    const next = !isCameraOn;
    if (v.length > 0) v[0].enabled = next;
    if (a.length > 0) { a[0].enabled = next; setIsMicOn(next); }
    setIsCameraOn(next);
  };
  const toggleSpeaker = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (remoteVideoRef.current) {
      remoteVideoRef.current.muted = isSpeakerOn;
      setIsSpeakerOn(!isSpeakerOn);
    }
  };

  // ── Akcje sterujące ─────────────────────────────────────────────────────
  // Bez logOut/login — istniejąca sesja zostaje podtrzymana. Websocket wykryje zmianę trasy
  // i wyśle switchMode → backend wyprowadzi usera z kolejki ruletki i dołączy do pokoju.
  const goHome = () => {
    try { cleanup(); } catch (e) { /* ignore */ }
    const targetRoomId = context.publicRooms?.[0]?.id || '1';
    navigate('/chat', {
      state: {
        room: targetRoomId,
        name: context.user?.name || 'guest',
        gender: context.user?.gender || 'male',
      }
    });
  };

  const handleApplyFilters = (filters: IRouletteFilters) => {
    setContext(prev => ({ ...prev, roulette: { ...prev.roulette, filters } }));
    window.dispatchEvent(new CustomEvent('rouletteFiltersUpdate', { detail: filters }));
    setIsFiltersOpen(false);
  };

  return (
    <div
      data-component="RouletteContainer"
      className={classNames(styles.rouletteContainer, { [styles.chatOpen]: isChatOpen })}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onWheel={onWheel}
    >
      {/* Pełnoekranowe wideo */}
      <div className={styles.videoStage}>
        <video ref={remoteVideoRef} className={styles.remoteVideo} playsInline autoPlay />
        <div className={styles.localVideoWrapper}>
          <video ref={localVideoRef} className={styles.localVideo} playsInline autoPlay muted />
        </div>

        {/* Loader podczas oczekiwania / nawiązywania połączenia */}
        {status !== 'matched' && (
          <div className={styles.waitingOverlay}>
            <div className={styles.spinner} />
            <div className={styles.waitingText}>
              {status === 'waiting' ? 'Łączenie...' : 'Wchodzisz do ruletki…'}
            </div>
          </div>
        )}

        {/* Nazwa rozmówcy */}
        {status === 'matched' && partner && (
          <div className={styles.partnerBadge}>
            <span className={styles.partnerName}>{partner.name}</span>
            <span className={styles.partnerGender}>
              {partner.gender === 'female' ? '♀' : '♂'}
            </span>
          </div>
        )}

        {/* Wskazówka swipe */}
        {status === 'matched' && !isChatOpen && (
          <div className={styles.swipeHint}>Przewiń w górę lub w dół, aby zmienić rozmówcę</div>
        )}
      </div>

      {/* Górny pasek: Home + filtry */}
      <div className={styles.topBar}>
        <button className={styles.topBtn} onClick={goHome} title="Wróć do czatu">
          <span className={styles.topBtnLabel}>Powró do czatu</span>
        </button>
        {isRegistered && (
          <button
            className={classNames(styles.topBtn, { [styles.active]: isFiltersOpen })}
            onClick={() => setIsFiltersOpen(v => !v)}
            title="Filtry"
          >
            <span className={styles.topBtnLabel}>Filtry</span>
          </button>
        )}
      </div>

      {isFiltersOpen && isRegistered && (
        <RouletteFilters
          publicRooms={context.publicRooms || []}
          filters={context.roulette.filters}
          onApply={handleApplyFilters}
          onClose={() => setIsFiltersOpen(false)}
        />
      )}

      {/* Dolny pasek kontrolek */}
      <div className={styles.bottomBar}>
        <button className={styles.iconBtn} onClick={toggleSpeaker}>
          {isSpeakerOn ? <VoiceIcon /> : <VoiceMuteIcon />}
        </button>
        <button className={styles.iconBtn} onClick={toggleMic}>
          {isMicOn ? <MicrophoneIcon /> : <MicrophoneMuteIcon />}
        </button>
        <button className={styles.iconBtn} onClick={toggleCamera}>
          {isCameraOn ? <CameraIcon /> : <CameraMuteIcon />}
        </button>
        {/* <button
          className={classNames(styles.iconBtn, styles.nextBtn)}
          onClick={requestNext}
          title="Następny rozmówca"
        >
          <span className={styles.nextLabel}>Dalej</span>
        </button> */}
        <button
          className={classNames(styles.iconBtn, { [styles.active]: isChatOpen })}
          onClick={() => setIsChatOpen(v => !v)}
        >
          <ChatIcon className={styles.chatIcon} />
        </button>
      </div>

      {/* Slide-up panel czatu (~40% wysokości) */}
      <div className={classNames(styles.chatPanel, { [styles.chatPanelOpen]: isChatOpen })}>
        <div className={styles.chatHeader}>
          <span className={styles.chatTitle}>Czat z {partner?.name || ''}</span>
          <button className={styles.chatCloseBtn} onClick={() => setIsChatOpen(false)}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.chatBody}>
          <RouletteChat />
        </div>
      </div>
    </div>
  );
}

export default RouletteContainer;
